import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import {
  GuidePermissionKey,
  PermissionManagerService,
} from "./permission-manager.service";
import { PermissionStepperComponent } from "../shared/permissions/permission-stepper.component";
import { LocalStorageService } from "./localStorage.service";
import { RoleHelperService } from "./role-helper.service";
import { AppService } from "./app.service";
import {
  ModalQueuePriority,
  ModalQueueService,
} from "./modal-queue.service";

@Injectable({
  providedIn: "root",
})
export class PermissionStepperService {
  private readonly COOLDOWN_KEY = "permission_stepper_last_reprompt_at";
  private readonly COOLDOWN_MS = 60 * 60 * 1000;
  private readonly STORAGE_KEY = "permission_stepper_state_v1";
  private readonly guideFlowKeys: GuidePermissionKey[] = [
    "location",
    "notifications",
    "battery",
  ];

  constructor(
    private localStorage: LocalStorageService,
    private roleHelper: RoleHelperService,
    private permissionManager: PermissionManagerService,
    private appService: AppService,
    private modalQueue: ModalQueueService
  ) {}

  async maybeLaunchOnStart(): Promise<void> {
    if (!this.isNativePlatform()) return;
    if (!this.isPrestataire()) return;
    if (this.isCompleted()) return;
    const missing = await this.getMissingPermissions();
    if (!missing.length) return;
    await this.presentStepper(missing);
  }

  async checkAndPresentIfRevoked(): Promise<void> {
    if (!this.isNativePlatform()) return;
    if (!this.isPrestataire()) return;
    if (!this.isCompleted()) return;
    if (!this.canReprompt()) return;
    const missing = await this.getMissingPermissions();
    if (!missing.length) return;
    this.resetStoredStepperState();
    const shown = await this.presentStepper(missing);
    if (shown) {
      localStorage.setItem(this.COOLDOWN_KEY, String(Date.now()));
    }
  }

  async checkDayStartAndMaybePresentStepper(): Promise<boolean> {
    if (!this.isNativePlatform()) return true;
    if (!this.isPrestataire()) return true;

    const state = await this.permissionManager.checkPermissionState();
    console.log('[PermissionStepper] checkDayStartAndMaybePresentStepper state', state);

    // Hard block: no location access at all — the app cannot function without it.
    const locationStatus = state.locationStatus;
    if (locationStatus === "denied" || locationStatus === "notDetermined") {
      console.warn('[PermissionStepper] hard block — location denied/notDetermined');
      this.resetStoredStepperState();
      // Use forcePresent so the stepper shows immediately regardless of other modals in queue.
      await this.presentStepper(["location"], true);
      return false;
    }

    // Soft guidance: show the stepper for any advisory missing permissions
    // (battery optimization, "always" upgrade, notifications) but do NOT block start.
    // The stepper is advisory — user can dismiss and still proceed.
    const missing = this.guideFlowKeys.filter(
      (key) => !this.permissionManager.isGranted(key, state)
    );
    if (missing.length) {
      console.log('[PermissionStepper] soft-missing permissions (advisory, non-blocking):', missing);
      this.resetStoredStepperState();
      // Fire-and-forget: show stepper in background, do not await the result for the gate.
      this.presentStepper(missing, false).catch(() => {});
    }

    return true;
  }

  /**
   * @param filterKeys  Permission keys to show in filtered/single-step mode.
   * @param hardBlock   When true, the stepper is shown with CRITICAL priority and bypasses
   *                    the "wait for no modal on top" gate — the location hard-block MUST show.
   */
  private async presentStepper(filterKeys?: GuidePermissionKey[], hardBlock = false): Promise<boolean> {
    this.appService.dispatchModal("BACKGROUND_LOCATION_ALWAYS_MODAL", false);
    this.appService.setPermissionGuideModalOpen(true);
    try {
      const id = hardBlock ? "permission-stepper-hardblock" : "permission-stepper";
      console.log(`[PermissionStepper] presentStepper — enqueueing (id=${id}, hardBlock=${hardBlock})`);
      const dismissed = await this.modalQueue.enqueueModal(
        {
          component: PermissionStepperComponent,
          cssClass: "permission-modal",
          backdropDismiss: false,
          canDismiss: true,
          componentProps: filterKeys?.length ? { filterKeys } : {},
        },
        {
          id,
          priority: hardBlock ? ModalQueuePriority.CRITICAL : ModalQueuePriority.NORMAL,
          forcePresent: hardBlock,
        }
      );
      console.log(`[PermissionStepper] presentStepper — modal dismissed (id=${id}):`, dismissed);
      return dismissed !== undefined;
    } catch (e) {
      console.error('[PermissionStepper] presentStepper — modal queue error:', e);
      return false;
    } finally {
      this.appService.setPermissionGuideModalOpen(false);
    }
  }

  private async getMissingPermissions(): Promise<GuidePermissionKey[]> {
    const state = await this.permissionManager.checkPermissionState();
    return this.guideFlowKeys.filter((key) => !this.permissionManager.isGranted(key, state));
  }

  private isPrestataire(): boolean {
    const user = this.localStorage.getUserData()?.client;
    return this.roleHelper.isPrestataireUser(user);
  }

  private canReprompt(): boolean {
    const raw = localStorage.getItem(this.COOLDOWN_KEY);
    const last = raw ? Number(raw) : 0;
    if (!last) return true;
    return Date.now() - last >= this.COOLDOWN_MS;
  }

  private resetStoredStepperState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  private isCompleted(): boolean {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { completed?: boolean };
      return !!parsed.completed;
    } catch {
      return false;
    }
  }

  private isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }
}
